import { Severity } from "node-library/dist/constants";
import {
	FrontendCompanySampleMatrix,
	FrontendCompanyTestCategory,
	FrontendCompanyWithAdditionalInfomation,
	FrontendSampleSpecification,
	FrontendSampleSpecificationDirectory,
	FrontendUserInfo,
} from "node-library/dist/portal";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { uploadAllToS3 } from "../../helpers/frontendFileOperationHelper";
import { sendPOSTRequest } from "../../requestHelper/sendRequest";
import { ButtonTextLoadingAnimation } from "../animation/ButtonLoadingAnimation";
import { GreenButton, GreyButton } from "../button";
import CreateSpecFileWithoutUploadModal from "../modals/createSpecFileWithoutUploadModal";
import CreateSpecificationNoteModalContent from "../modals/modalContents/createSpecificationNoteModalContent";
import UploadSpecificationFileModalUploader from "../modals/modalContents/uploadSpecificationFileModalUploader";
import { StepsBar } from "../steps-bar";
import { RedErrorIcon } from "../toast/errorIcons";
import { useSnackbar } from "../toast/showToastMessage";
import { GreenTickIcon } from "../toast/successIcons";
import { CustomDrawer } from "./drawer";
import UnsavedChangesModal from "../modals/unsavedChangesModal";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	setCompany: React.Dispatch<
		React.SetStateAction<FrontendCompanyWithAdditionalInfomation | undefined>
	>;
	user: FrontendUserInfo;
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;

	organizationId: string;
	testCategory: FrontendCompanyTestCategory;
	selectedMatrix: FrontendCompanySampleMatrix;
	currentFolderSpecifications: Array<FrontendSampleSpecification>;
	setFolderSpecifications: React.Dispatch<
		React.SetStateAction<Array<FrontendSampleSpecification>>
	>;

	specDirectories: Array<FrontendSampleSpecificationDirectory>;
	setSpecDirectories: React.Dispatch<
		React.SetStateAction<Array<FrontendSampleSpecificationDirectory>>
	>;

	setSelectedSpecificationId: (id: string) => void;
}

export default function CreateSpecificationAndReadingDrawer(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		company,
		setCompany,
		user,
		testCategory,
		open,
		setOpen,
		selectedMatrix,
		currentFolderSpecifications,
		organizationId,
		setFolderSpecifications,
		specDirectories,
		setSpecDirectories,
	} = props;

	const [currentPageIndex, setCurrentPageIndex] = useState<number>(1);

	const [currentFolder, setCurrentFolder] = useState<
		FrontendSampleSpecificationDirectory | undefined
	>();

	const [selectedSpecFile, setSelectedSpecFile] = useState<
		FrontendSampleSpecification | undefined
	>();

	const [uploadStarted, setUploadStarted] = useState<boolean>(false);
	const [fileToUpload, setFileToUpload] = useState<{
		file: File;
		progress: number;
	} | null>(null);

	const [newSpecFileModalOpen, setNewSpecFileModalOpen] =
		useState<boolean>(false);

	const [unsavedChangesModalOpen, setUnsavedChangesModalOpen] =
		useState<boolean>(false);
	const [unsavedChanges, setUnsavedChanges] = useState<boolean>(false);

	useEffect(() => {
		if (open) {
			setFileToUpload(null);
			setUploadStarted(false);
			setCurrentPageIndex(1);
			setSelectedSpecFile(undefined);
		}
	}, [open]);

	function setDrawerOpen(drawerOpen: boolean) {
		if (!drawerOpen && unsavedChanges) {
			setUnsavedChangesModalOpen(true);
		} else {
			setOpen(drawerOpen);
		}
	}

	function setFileProgress(progress: number) {
		setFileToUpload((prevFile) => {
			return prevFile ? { ...prevFile, progress: progress } : null;
		});
	}

	async function finalizeSubmit(
		uploadBatchId: string,

		errorFilename: Array<string>,
		errorSpecIds: Array<string>,
	) {
		try {
			const response = await sendPOSTRequest(
				"/specifications/finalize-specification-upload",
				{
					organizationId,
					uploadBatchId,
					errorSpecIds,
				},
			);

			const insertedSpecs: Array<FrontendSampleSpecification> =
				response.data.insertedSpecs;

			setFolderSpecifications((prevSpecs) => prevSpecs.concat(insertedSpecs));

			setSelectedSpecFile(insertedSpecs[0]);
			if (errorFilename.length === 0) {
				openSnackbar(
					translation("AllFilesUploadedSuccessfully"),
					Severity.SUCCESS,
					undefined,
					<GreenTickIcon />,
				);
				if (insertedSpecs.length > 0) {
					props.setSelectedSpecificationId(insertedSpecs[0].specificationId);
				}
				setTimeout(() => {
					setCurrentPageIndex(2);
				}, 1000);
			} else {
				openSnackbar(
					translation("SomeFilesFailedToUpload", {
						count: errorFilename.length,
					}),
					Severity.ERROR,
					translation("SomeFilesFailedToUploadMsg", {
						files: errorFilename.join(", "),
					}),
					<RedErrorIcon />,
				);
				setUploadStarted(false);
			}
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
			setUploadStarted(false);
		}
	}
	async function handleCreateEmptySpec(newFileName: string) {
		try {
			const response = await sendPOSTRequest(
				"/specifications/create-specification-without-upload",
				{
					testCategoryId: props.testCategory.testCategoryId,
					organizationId,
					matrixId: props.selectedMatrix.matrixId,
					fileName: newFileName,
				},
			);

			const insertedSpecs: Array<FrontendSampleSpecification> =
				response.data.insertedSpecs;

			setFolderSpecifications((prevSpecs) => prevSpecs.concat(insertedSpecs));
			setSelectedSpecFile(insertedSpecs[0]);

			openSnackbar(
				translation("SpecificationCreated"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />,
			);
			if (insertedSpecs.length > 0) {
				props.setSelectedSpecificationId(insertedSpecs[0].specificationId);
			}
			setTimeout(() => {
				setNewSpecFileModalOpen(false);
				setCurrentPageIndex(2);
			}, 1000);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
			setUploadStarted(false);
		}
		setCurrentPageIndex(2);
	}
	async function handleSubmit() {
		if (!fileToUpload) return; // Guard clause
		setUploadStarted(true);
		try {
			const response = await sendPOSTRequest(
				"/specifications/get-specification-presigned-upload-urls",
				{
					organizationId,
					testCategoryId: props.testCategory.testCategoryId,
					specFiles: [
						{
							// Just one file in the array
							fullName: fileToUpload.file.name,
							fileSize: fileToUpload.file.size,
							matrixId: props.selectedMatrix.matrixId,
							type: fileToUpload.file.type,
							specDirectoryId: currentFolder?.directoryId,
						},
					],
				},
			);

			const uploadBatchId: string = response.data.uploadBatchId;

			const uploadUrls: Array<{
				fileName: string;
				specificationId: string;
				presignedPost: AWS.S3.PresignedPost;
			}> = response.data.uploadUrls;
			const results = await uploadAllToS3(
				[fileToUpload],
				uploadUrls,
				setFileProgress,
			);

			//check if all uploads are successful
			const errorFilename: Array<string> = [];
			const errorSpecIds: Array<string> = [];

			[fileToUpload].forEach((file, index) => {
				if (results[index] == false) {
					let filename: string = file.file.name;
					const specId = uploadUrls.find((item) => {
						return item.fileName == filename;
					})?.specificationId as string;
					if (filename.length > 30) {
						filename = filename.substring(0, 30) + "...";
					}
					errorFilename.push(filename);
					errorSpecIds.push(specId);
				}
			});

			await finalizeSubmit(uploadBatchId, errorFilename, errorSpecIds);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		}
	}

	const getBreadcrumbs = () => {
		const crumbs: Array<FrontendSampleSpecificationDirectory> = [];
		let tempFolder = currentFolder;

		while (tempFolder) {
			crumbs.unshift(tempFolder); // Add to the beginning of array
			// Find the parent of the current temp folder
			tempFolder = specDirectories.find(
				(dir) => dir.directoryId === tempFolder?.parentDirectoryId,
			);
		}
		return crumbs;
	};

	const breadcrumbs = getBreadcrumbs();

	const headerItems = [
		translation("AddSpecification"),
		translation("SpecificationNote"),
	];

	const headerBar: React.ReactNode = (
		<div id="new-sample-header">
			<StepsBar
				items={headerItems}
				currentItem={currentPageIndex - 1}
				setCurrentItem={setCurrentPageIndex}
			></StepsBar>
		</div>
	);
	const uploader: React.ReactNode = (
		<UploadSpecificationFileModalUploader
			user={user}
			testCategory={testCategory}
			selectedMatrix={selectedMatrix}
			currentFolderSpecifications={currentFolderSpecifications}
			setFolderSpecifications={setFolderSpecifications}
			specDirectories={specDirectories}
			setSpecDirectories={setSpecDirectories}
			currentFolder={currentFolder}
			setCurrentFolder={setCurrentFolder}
			uploadStarted={uploadStarted}
			setUploadStarted={setUploadStarted}
			fileToUpload={fileToUpload}
			setFileToUpload={setFileToUpload}
		></UploadSpecificationFileModalUploader>
	);

	function updateSpecification(specification: FrontendSampleSpecification) {
		setFolderSpecifications((prevSpecs) => {
			// ADD 'return' here
			return prevSpecs.map((spec) => {
				if (spec.specificationId === specification.specificationId) {
					return specification;
				}
				return spec;
			});
		});
	}

	let currentPageConent: React.ReactNode;
	switch (currentPageIndex) {
		case 1:
			currentPageConent = (
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: "10px",
						margin: "0px 20px",
					}}
				>
					{uploader}
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							gap: "10px",
							marginTop: "20px",
						}}
					>
						<GreyButton
							key="spec-skip-and-next-button"
							id="spec-skip-and-next-button"
							size="flex"
							children={
								uploadStarted ? (
									<ButtonTextLoadingAnimation
										text={translation("Uploading")}
									></ButtonTextLoadingAnimation>
								) : (
									translation("CreateSpecWithoutFile")
								)
							}
							disabled={uploadStarted}
							onClick={() => {
								setNewSpecFileModalOpen(true);
							}}
						></GreyButton>
						<GreenButton
							key="spec-upload-and-next-button"
							id="spec-upload-and-next-button"
							size="flex"
							children={
								uploadStarted ? (
									<ButtonTextLoadingAnimation
										text={translation("Uploading")}
									></ButtonTextLoadingAnimation>
								) : (
									translation("UploadAndNext")
								)
							}
							disabled={uploadStarted || !fileToUpload}
							onClick={() => {
								handleSubmit();
							}}
						></GreenButton>
					</div>
					,
				</div>
			);
			break;
		case 2:
			currentPageConent = (
				<div>
					<CreateSpecificationNoteModalContent
						user={user}
						company={company}
						setCompany={setCompany}
						testCategory={testCategory}
						selectedMatrix={selectedMatrix}
						updateSpecification={updateSpecification}
						currentFolderBreadcrumbs={breadcrumbs}
						selectedSpecFile={selectedSpecFile as FrontendSampleSpecification}
						setUnsavedChanges={setUnsavedChanges}
					></CreateSpecificationNoteModalContent>
					<div style={{ display: "flex", justifyContent: "center" }}>
						<GreenButton
							id={"spec-finish-button"}
							size="small"
							onClick={() => {
								setDrawerOpen(false);
							}}
						>
							{translation("Finish")}
						</GreenButton>
					</div>
				</div>
			);
			break;
	}
	const uploadSpecificationDrawer: React.ReactNode = (
		<CustomDrawer
			anchor={"right"}
			open={open}
			setOpen={setDrawerOpen}
			headerText={translation("CreateSpecification")}
			headerDescription={translation("CreateSpecificationDescription")}
			drawerWidth={`100%`}
		>
			<div style={{ display: "flex", justifyContent: "center" }}>
				<div style={{ width: "50%" }}>{headerBar}</div>
			</div>
			{currentPageConent}
		</CustomDrawer>
	);

	return (
		<>
			{uploadSpecificationDrawer}
			{
				<CreateSpecFileWithoutUploadModal
					open={newSpecFileModalOpen}
					setOpen={setNewSpecFileModalOpen}
					isEdit={false}
					handleSubmit={handleCreateEmptySpec}
				></CreateSpecFileWithoutUploadModal>
			}
			{
				<UnsavedChangesModal
					open={unsavedChangesModalOpen}
					setOpen={setUnsavedChangesModalOpen}
					setUnsavedChanges={setUnsavedChanges}
					proceedAction={() => {
						setUnsavedChanges(false);
						setOpen(false);
					}}
				></UnsavedChangesModal>
			}
		</>
	);
}
